import { Decimal, ZERO as ZERO_IMPORT } from "./decimal-config";

export const ZERO = ZERO_IMPORT;

/**
 * Converts a value to a Decimal instance safely
 * @param value - The value to convert (string, number, or Decimal)
 * @returns A Decimal instance
 */
export function toDecimal(value: string | number | Decimal): Decimal {
  if (value instanceof Decimal) {
    return value;
  }

  if (typeof value === "string") {
    // Handle German number format (comma as decimal separator)
    // First check if it's already in standard format (contains dot as decimal separator)
    if (value.includes(".") && !value.includes(",")) {
      return new Decimal(value);
    }
    // Handle German format (comma as decimal separator, dots as thousands separators)
    const normalizedValue = value.replace(/\./g, "").replace(",", ".");
    if (normalizedValue !== "") {
      return new Decimal(normalizedValue);
    }
    return new Decimal("0");
  }

  return new Decimal(value);
}

/**
 * Converts a value to a number safely, handling precision issues
 * @param value - The value to convert
 * @returns A number with proper precision
 */
export function toNumber(value: string | number | Decimal): number {
  return toDecimal(value).toNumber();
}

/**
 * Formats a number for display with custom separators and decimal rendering.
 * @param value - The value to format.
 * @param precision - Number of decimal places (default: 6), or null to use native decimals.
 * @param options - Optional configuration object.
 * @param options.numberSeparators - Custom decimal and thousand separators (default: { decimal: ",", thousand: "." }).
 * @param options.alwaysRenderDecimals - Whether to always show decimals, even if they are zero (e.g., 3.00 instead of 3).
 * @returns Formatted string.
 */
export function formatNumber(
  value: string | number | Decimal,
  precision: number | null = 6,
  options?: {
    numberSeparators?: { decimal: "," | "."; thousand: "," | "." };
    alwaysRenderDecimals?: boolean;
  },
): string {
  const decimal = toDecimal(value);

  // Handle special cases
  if (decimal.isNaN()) return "NaN";
  if (!decimal.isFinite()) return decimal.isPositive() ? "∞" : "-∞";

  // Determine locale and separators
  const separators = options?.numberSeparators || { decimal: ",", thousand: "." };
  const locale = separators.decimal === "," ? "de-DE" : "en-US";

  // Build the Intl.NumberFormat options object
  const formatOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: options?.alwaysRenderDecimals && precision !== null ? precision : 0,
    // When precision is null, render all native decimals. Without an explicit
    // maximumFractionDigits, Intl defaults to 3, which would silently round
    // small values (e.g. 0.00028 → "0").
    maximumFractionDigits: precision !== null ? precision : 20,
  };

  // Use a different approach for formatting based on options to ensure correct behavior.
  const number = decimal.toNumber();
  const formattedNumber = new Intl.NumberFormat(locale, formatOptions).format(number);

  // Custom separator replacement if needed for mixed locales
  if (locale === "en-US" && separators.decimal === ",") {
    return formattedNumber.replace(/\./g, "#").replace(/,/g, ".").replace(/#/g, ",");
  }

  if (locale === "de-DE" && separators.decimal === ".") {
    return formattedNumber.replace(/,/g, "#").replace(/\./g, ",").replace(/#/g, ".");
  }

  return formattedNumber;
}

/**
 * Parses a string value to a number, handling German number format
 * @param value - The string value to parse
 * @returns The parsed number or 0 if invalid
 */
export function parseNumber(value: string): number {
  if (!value || value.trim() === "") return 0;

  try {
    // Trim whitespace
    const trimmedValue = value.trim();

    // Handle German number format (comma as decimal separator)
    // First check if it's already in standard format (contains dot as decimal separator)
    if (trimmedValue.includes(".") && !trimmedValue.includes(",")) {
      const decimal = new Decimal(trimmedValue);
      if (decimal.isNaN()) return 0;
      return decimal.toNumber();
    }
    // Handle German format (comma as decimal separator, dots as thousands separators)
    const normalizedValue = trimmedValue.replace(/\./g, "").replace(",", ".");
    const decimal = new Decimal(normalizedValue);

    if (decimal.isNaN()) return 0;
    return decimal.toNumber();
  } catch {
    return 0;
  }
}

/**
 * Adjusts decimal precision of a value
 * @param value - The value to adjust
 * @param precision - The number of decimal places
 * @returns Formatted string with the specified precision
 */
export function adjustDecimalPrecision(value: string | number | Decimal, precision: number): string {
  const decimal = toDecimal(value);

  if (decimal.isNaN()) return "0";
  if (!decimal.isFinite()) return decimal.isPositive() ? "∞" : "-∞";

  return decimal.toNumber().toLocaleString("de-DE", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

/**
 * Performs arithmetic operations with proper decimal precision
 */
export const DecimalMath = {
  add: (a: string | number | Decimal, b: string | number | Decimal): number => {
    return toDecimal(a).plus(toDecimal(b)).toNumber();
  },

  subtract: (a: string | number | Decimal, b: string | number | Decimal): number => {
    return toDecimal(a).minus(toDecimal(b)).toNumber();
  },

  multiply: (a: string | number | Decimal, b: string | number | Decimal): number => {
    return toDecimal(a).times(toDecimal(b)).toNumber();
  },

  divide: (a: string | number | Decimal, b: string | number | Decimal): number => {
    return toDecimal(a).dividedBy(toDecimal(b)).toNumber();
  },

  round: (value: string | number | Decimal, precision: number = 0): number => {
    return toDecimal(value).toDecimalPlaces(precision).toNumber();
  },

  floor: (value: string | number | Decimal): number => {
    return toDecimal(value).floor().toNumber();
  },

  ceil: (value: string | number | Decimal): number => {
    return toDecimal(value).ceil().toNumber();
  },
};

/**
 * Validates if a string represents a valid number in German format
 * @param value - The string to validate
 * @returns True if valid, false otherwise
 */
export function isValidNumber(value: string): boolean {
  if (!value || value.trim() === "") return false;

  try {
    const normalizedValue = value.replace(/\./g, "").replace(",", ".");
    const decimal = new Decimal(normalizedValue);
    return !decimal.isNaN();
  } catch {
    return false;
  }
}

/**
 * Normalizes a number string from German format to standard format
 * @param value - The German formatted number string
 * @returns Normalized number string
 */
export function normalizeNumberString(value: string): string {
  // First check if it's already in standard format (contains dot as decimal separator)
  if (value.includes(".") && !value.includes(",")) {
    return value;
  }
  // Handle German format (comma as decimal separator, dots as thousands separators)
  return value.replace(/\./g, "").replace(",", ".");
}

/**
 * Converts a standard number string to German format
 * @param value - The standard number string
 * @returns German formatted number string
 */
export function toGermanFormat(value: string): string {
  const decimal = toDecimal(value);
  return decimal.toNumber().toLocaleString("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}
